import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Send, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success(t('contact.form.success'), {
      description: t('contact.form.successDesc'),
    });
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    });
    setIsSubmitting(false);
  };

  // Google Maps embed URL for Abu Hail, Deira, Dubai
  const googleMapsEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3607.5837881380286!2d55.33499731501!3d25.276987983868!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5cc4a5e5e5e5%3A0x5e5e5e5e5e5e5e5e!2sAbu%20Hail%20-%20Deira%20-%20Dubai!5e0!3m2!1sen!2sae!4v1234567890";
  
  const googleMapsDirectionsUrl = "https://www.google.com/maps/search/?api=1&query=Royal+House+Building+Abu+Hail+Deira+Dubai+UAE";

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-slate-50 to-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-blue-50 text-brand-blue text-sm font-semibold rounded-full mb-4">
            {t('contact.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            {t('contact.title1')}
            <span className="text-brand-red"> {t('contact.title2')}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('contact.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Contact Info & Map */}
          <div className={`space-y-6 ${isRTL ? 'lg:order-2' : ''}`}>
            {/* Contact Information Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Office Address */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">{t('contact.location')}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {t('contact.address.line1')}<br />
                      {t('contact.address.line2')}<br />
                      {t('contact.address.line3')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Numbers */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-red" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">{t('contact.phone')}</h3>
                    <div className="space-y-1">
                      <a 
                        href="tel:+971542808859" 
                        className="block text-sm text-slate-600 hover:text-brand-blue transition-colors"
                        dir="ltr"
                      >
                        +971 54 280 8859
                      </a>
                      <a 
                        href="tel:+971502951774" 
                        className="block text-sm text-slate-600 hover:text-brand-blue transition-colors"
                        dir="ltr"
                      >
                        +971 50 295 1774
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">{t('contact.email')}</h3>
                    <a 
                      href="mailto:info@afaqalnabth.com" 
                      className="text-sm text-slate-600 hover:text-brand-blue transition-colors"
                      dir="ltr"
                    >
                      info@afaqalnabth.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-brand-red" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">{t('contact.hours')}</h3>
                    <div className="text-sm text-slate-600 space-y-1">
                      <div className="flex justify-between gap-4">
                        <span>{t('contact.monFri')}</span>
                        <span className="font-medium text-slate-700" dir="ltr">9:00 AM – 6:00 PM</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>{t('contact.satSun')}</span>
                        <span className="font-medium text-brand-red">{t('contact.closed')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-brand-blue" />
                  {t('contact.findUs')}
                </h3>
                <a 
                  href={googleMapsDirectionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-brand-blue hover:text-brand-red transition-colors font-medium"
                >
                  {t('contact.getDirections')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="relative h-64 sm:h-72 bg-slate-100">
                <iframe
                  src={googleMapsEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="AFAQ ALNABTH Location - Abu Hail, Deira, Dubai"
                  className="absolute inset-0"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className={`${isRTL ? 'lg:order-1' : ''}`}>
            <form onSubmit={handleSubmit} className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-lg shadow-slate-100/50 h-full">
              <h3 className="text-xl font-semibold text-slate-800 mb-6">
                {isRTL ? 'أرسل لنا رسالة' : 'Send us a Message'}
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.name')} *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.namePlaceholder')}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.email')} *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                    className="h-12"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+971 XX XXX XXXX"
                    className="h-12"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.form.company')}
                  </label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.form.companyPlaceholder')}
                    className="h-12"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('contact.form.message')} *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.messagePlaceholder')}
                  required
                  rows={5}
                  className="resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-red hover:bg-red-700 text-white py-6 text-base shadow-lg shadow-red-600/20 hover:shadow-red-600/30 transition-all duration-300 disabled:opacity-70"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('contact.form.sending')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                    {t('contact.form.submit')}
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
